import { Textarea } from '@/components/ui/Textarea';
import { QualificationSectionCard } from '@/components/profile/QualificationSectionCard';
import {
  DEPTH_LABELS,
  THEME_LABELS,
  addItem,
  editItem,
  removeItem,
  setItemStatus,
  setTheme,
} from '@/lib/company-qualification';
import {
  type CompanyQualificationSheet,
  type QualificationSectionKey,
  type QualificationThemeKey,
  type ThemeDepth,
  THEME_DEPTHS,
} from '@shared/types/company-qualification';

interface Props {
  sheet: CompanyQualificationSheet;
  onChange: (sheet: CompanyQualificationSheet) => void;
  /** A theme of the sheet, or null for the synthesis sections. */
  theme: QualificationThemeKey | null;
  sections: readonly QualificationSectionKey[];
}

/** One theme of the sheet: its depth, its short synthesis, then its sections. */
export const QualificationThemeBlock = ({ sheet, onChange, theme, sections }: Props) => (
  <section className="space-y-4 rounded-xl border border-domino-cardBorder bg-domino-warm p-5">
    {theme ? (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-lg font-semibold text-domino-ink">{THEME_LABELS[theme].label}</h3>
          <select
            aria-label="Approfondissement"
            value={sheet.themes[theme].depth}
            onChange={(event) => onChange(setTheme(sheet, theme, { depth: event.target.value as ThemeDepth }))}
            className="h-8 rounded-lg border border-domino-cardBorder bg-domino-warm px-2 text-xs text-domino-ink"
          >
            {THEME_DEPTHS.map((depth) => <option key={depth} value={depth}>{DEPTH_LABELS[depth]}</option>)}
          </select>
        </div>
        <Textarea
          aria-label="Synthèse du thème"
          value={sheet.themes[theme].summary}
          onChange={(event) => onChange(setTheme(sheet, theme, { summary: event.target.value }))}
          rows={2}
          placeholder={`Synthèse : ${THEME_LABELS[theme].deliverable}`}
          className="text-sm"
        />
      </div>
    ) : (
      <div>
        <h3 className="font-display text-lg font-semibold text-domino-ink">Synthèse</h3>
        <p className="text-xs text-domino-inkSoft">Ce que les thèmes disent ensemble, avant la proposition.</p>
      </div>
    )}
    {sections.map((key) => (
      <QualificationSectionCard
        key={key}
        sectionKey={key}
        items={sheet.sections[key]}
        onAdd={(text) => onChange(addItem(sheet, key, text))}
        onEdit={(itemId, text) => onChange(editItem(sheet, key, itemId, text))}
        onStatus={(itemId, status) => onChange(setItemStatus(sheet, key, itemId, status))}
        onRemove={(itemId) => onChange(removeItem(sheet, key, itemId))}
      />
    ))}
  </section>
);
